/**
 * SRES · Unified 3D Cockpit (Sprint P24 · Feb 2026)
 * ══════════════════════════════════════════════════
 *
 * P0 · 3D VIEWER CORRECTION (Feb 2026)
 * ────────────────────────────────────
 * Fixes the "block model renders as a tiny dot" bug across every project.
 *
 *   · Shared `worldToScene` conversion is used by every layer (blocks,
 *     drillholes, mine design, geo mesh, assay discs, section slicer).
 *   · Scene origin is computed once per (project, estimation-run) from the
 *     block-model summary bbox — remains stable across cut-off, filter,
 *     layer or camera changes.
 *   · Custom `FitCamera` component uses vertical + horizontal FOV distance
 *     solver; supports six fit modes (Active Model · All Data · Ore · Pit
 *     · Selected · Reset) plus six canonical views (Top / Front / East /
 *     North / Iso-NE / Iso-SW).
 *   · Regional-outlier holes stay excluded from Fit Active Model via a
 *     buffered block bbox (buffer = 25 % of model span).
 *   · Cut-off initialisation order: URL → estimation-run summary →
 *     project.cutoff_grade → commodity fallback. Visual sensitivity cut-off
 *     is clearly distinguished from the official reporting cut-off.
 *   · SuperAdmin-only diagnostics panel with scene origin, local & world
 *     bounding boxes, instance counts, invalid-coord counts, camera state.
 *   · Loading/fitting/error states — no more silent blank canvas.
 *
 * Prior sprints preserved:
 *   P15 ribbon menu · Project Explorer · scientific colormaps · XYZ grid
 *   P16 EW/NS Section Slicer with plane visualisation · Deep-linking
 *   P19.1 rich block tooltip · classification / z-slab / min-samples filters
 *   P20 Mine design overlay layer (open-cast only)
 *   P21 Geological modelling meshes + assay discs
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Canvas, useThree } from "@react-three/fiber";
import {
  OrbitControls, GizmoHelper, GizmoViewport, Grid, Html,
} from "@react-three/drei";
import * as THREE from "three";
import {
  ArrowsHorizontal, ArrowsVertical, Info, ArrowUp, Cube, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { api, getGeoModel } from "@/lib/api";
import {
  worldToScene, sceneToWorld, isValidCoordinate,
  computeSceneOriginFromPoints, computeLocalBoundingBox,
  fitDistanceForBox, VIEW_DIRECTIONS, bufferedBBox, pointInBox,
} from "@/lib/sceneCoords";
import MineDesignLayer, { mineDesignPointStream }
  from "@/pages/cockpit/MineDesignLayer";
import GeoMeshLayer from "@/pages/cockpit/GeoMeshLayer";
import AssayDiscsLayer from "@/pages/cockpit/AssayDiscsLayer";
import CockpitGeoWorkspace from "@/pages/cockpit/CockpitGeoWorkspace";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Sprint P32 · Extracted Cockpit modules
import { COLORMAPS, pickColour } from "@/pages/cockpit/utils/colorPalettes";
import { BlockMesh } from "@/pages/cockpit/scene/BlockModelLayer";
import GradeShellMesh from "@/pages/cockpit/scene/GradeShellMesh";
import ImplicitWireframeMesh from "@/pages/cockpit/scene/ImplicitWireframeMesh";
import { DrillholeTraces } from "@/pages/cockpit/scene/DrillholeTraces";
import { DrillholeMesh3D, sampleColour }
  from "@/pages/cockpit/scene/DrillholeIntervalLayer";
import { CollarLabels } from "@/pages/cockpit/scene/CollarLabelsLayer";
import { SlicerPlane } from "@/pages/cockpit/scene/SectionPlaneLayer";
import { FitCamera, CameraBridge, CameraTelemetry }
  from "@/pages/cockpit/scene/CameraHelpers";
import { SelectedBlockOutline }
  from "@/pages/cockpit/scene/SelectionHighlightLayer";
import { SnapshotBridge } from "@/pages/cockpit/hooks/useCockpitSnapshot";
// Iter98k · Behaviour-preserving extraction of the six Cockpit data
// queries — same query keys / options as the previous inline calls.
import { useCockpitQueries } from "@/pages/cockpit/hooks/useCockpitQueries";
import { ViewportToolbar } from "@/pages/cockpit/viewport/ViewportToolbar";
import { CapturedViewsGallery } from "@/pages/cockpit/CapturedViewsGallery";
import { ProjectExplorer } from "@/pages/cockpit/panels/ProjectExplorer";
import { InspectorPanel } from "@/pages/cockpit/panels/InspectorPanel";
import { SavedViewsPanel } from "@/pages/cockpit/panels/SavedViewsPanel";
import { BlockTooltipRows } from "@/pages/cockpit/panels/BlockTooltipRows";
import { BlockLegend, DrillholeLegend }
  from "@/pages/cockpit/panels/LegendPanel";
import { DiagnosticsPanel } from "@/pages/cockpit/panels/DiagnosticsPanel";

// ══════════════════════════════════════════════════════════════════════
// Main Cockpit page.
// ══════════════════════════════════════════════════════════════════════
export default function CockpitPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const isSuperAdmin = user?.role === "superadmin" || user?.role === "admin";

  // ── URL deep-linking (Sprint P16) ─────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const initPalette = COLORMAPS[searchParams.get("palette")] ? searchParams.get("palette") : "spectral";
  const urlCutoffRaw = searchParams.get("cutoff");
  const urlHasCutoff = urlCutoffRaw != null && urlCutoffRaw !== "";
  const initSlicer  = ["off","ns","ew"].includes(searchParams.get("slicer"))
    ? searchParams.get("slicer") : "off";
  const initSlicerPos = Number(searchParams.get("slicerPos") ?? 0);
  const initLayers = (() => {
    const raw = searchParams.get("layers");
    if (!raw) return { blocks: true, ore: true, waste: false, traces: true, labels: false, grid: true, shell: false, wireframe: false };
    const keys = raw.split(",");
    return {
      blocks: keys.includes("blocks"),   ore:    keys.includes("ore"),
      waste:  keys.includes("waste"),    traces: keys.includes("traces"),
      labels: keys.includes("labels"),   grid:   keys.includes("grid"),
      shell:  keys.includes("shell"),    wireframe: keys.includes("wireframe"),
    };
  })();

  const [palette, setPalette]       = useState(initPalette);
  const [cutoff, setCutoff]         = useState(urlHasCutoff ? Number(urlCutoffRaw) : 0);
  const [cutoffMode, setCutoffMode] = useState("auto");   // auto | manual
  const cutoffInitRef               = useRef(urlHasCutoff);   // true → don't override from run

  const [slicer, setSlicer]         = useState(initSlicer);
  const [slicerPos, setSlicerPos]   = useState(initSlicerPos);
  const [layers, setLayers]         = useState(initLayers);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const qc = useQueryClient();
  const [shareStatus, setShareStatus]   = useState("idle");
  const takeSnapshotRef = useRef(null);
  const cameraCtlRef    = useRef({ capture: () => ({}), apply: () => {} });

  const [colourBy, setColourBy]     = useState("grade");
  const [holeColourBy, setHoleColourBy] = useState("lithology");
  // ── Sprint P37y (Feb 2026) · Drillhole 3D integration ─────────────
  // Deep-link a specific hole (+ optional interval) via URL, so the
  // Log Workspace "View in 3D" action can focus the viewer directly.
  const initHoleId = searchParams.get("hole") || null;
  const initHighlightFrom = searchParams.get("from") ? Number(searchParams.get("from")) : null;
  const initHighlightTo   = searchParams.get("to")   ? Number(searchParams.get("to"))   : null;
  const [selectedHoleId, setSelectedHoleId] = useState(initHoleId);
  const [highlightedInterval, setHighlightedInterval] = useState(
    (initHoleId && initHighlightFrom != null && initHighlightTo != null)
      ? { hole_id: initHoleId, from: initHighlightFrom, to: initHighlightTo }
      : null
  );
  const [minSamples, setMinSamples] = useState(0);
  const [zSlab, setZSlab]           = useState(null);
  const [classFilter, setClassFilter]  = useState(new Set());
  const [domainFilter, setDomainFilter] = useState(new Set());
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [hoverPos, setHoverPos]         = useState({ x: 0, y: 0 });
  const [selectedBlock, setSelectedBlock] = useState(null);

  const [vExag, setVExag]           = useState(1);
  const [vExagMode, setVExagMode]   = useState("preset");

  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [includeOutlierHoles, setIncludeOutlierHoles] = useState(false);

  const overlayParam = searchParams.get("overlay");
  const [showMineDesignPit, setShowMineDesignPit] = useState(
    overlayParam === "mine-design",
  );

  const [geoWorkspaceOpen, setGeoWorkspaceOpen] = useState(false);
  const [geoWorkspacePinned, setGeoWorkspacePinned] = useState(true);
  const [geoVisibility, setGeoVisibility] = useState({});
  const [showAssayDiscs, setShowAssayDiscs] = useState(false);
  const [geoLoadedMeshes, setGeoLoadedMeshes] = useState({});

  // ── Fit state ─────────────────────────────────────────────────────
  const [fitMode, setFitMode]         = useState("active");
  const [fitRequest, setFitRequest]   = useState(null);
  const [fitBusy, setFitBusy]         = useState(false);
  const [fitError, setFitError]       = useState(null);
  const [manualCamera, setManualCamera] = useState(false);
  const [pendingViewDir, setPendingViewDir] = useState(null);

  // ── Data queries (extracted to useCockpitQueries in Iter98k) ─────
  const { projectQ, bmQ, holesQ, mineSummaryQ, pitDesignQ,
          geoSummaryQ, assayDiscsQ } =
    useCockpitQueries(pid, { showMineDesignPit, showAssayDiscs });

  useEffect(() => {
    let cancelled = false;
    const wanted = (geoSummaryQ.data?.models || [])
      .filter((m) => geoVisibility[m.id] === true && !geoLoadedMeshes[m.id])
      .map((m) => m.id);
    if (!wanted.length) return;
    (async () => {
      for (const id of wanted) {
        try {
          const full = await getGeoModel(pid, id);
          if (cancelled) return;
          setGeoLoadedMeshes((prev) => ({ ...prev, [id]: full }));
        } catch (e) {
          console.warn("geo-model load failed", id, e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [geoSummaryQ.data, geoVisibility, geoLoadedMeshes, pid]);

  // Sprint P31 · Auto-enable a sensible default set of geo layers on first
  // Sprint P32.1 · Presentation-grade default view.
  //
  // Per user directive (P0 · REMOVE PERMANENT GREEN INFLUENCE VOLUME):
  // The Cockpit must open like professional mining software — no debug
  // geometry, no auto-enabled grade shells, no search prisms. Only the
  // "core" geological objects auto-render:
  //
  //     · Topography / DEM             (auto-on when present)
  //     · Block model                  (always visible)
  //     · Drillholes + collars         (always visible)
  //     · Pit shell + boundary         (auto-on when present, via MineDesignLayer)
  //
  // The following are hidden by default and require an explicit user
  // toggle in the layer panel:
  //
  //     · grade_shell / implicit models          — user must opt-in
  //     · lithology_solid / explicit models      — user must opt-in
  //     · assay_discs                            — already opt-in
  //     · search / influence ellipsoid           — diagnostic only
  const geoDefaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (geoDefaultsAppliedRef.current) return;
    const models = geoSummaryQ.data?.models || [];
    if (!models.length) return;
    geoDefaultsAppliedRef.current = true;
    const initial = {};
    // Only auto-enable topography — the one non-debug backdrop layer.
    const topo = models.find((m) => m.kind === "topography");
    if (topo) initial[topo.id] = true;
    if (Object.keys(initial).length) {
      setGeoVisibility((prev) => ({ ...prev, ...initial }));
    }
  }, [geoSummaryQ.data]);

  const toggleGeoVisibility = (id) =>
    setGeoVisibility((prev) => ({ ...prev, [id]: prev[id] === true ? false : true }));

  // ── Derived data ──────────────────────────────────────────────────
  const bmSummary = bmQ.data?.summary || {};
  const blocks = useMemo(() => {
    const bm = bmQ.data;
    if (!bm) return [];
    return [...(bm.ore_blocks || []), ...(bm.waste_blocks || [])];
  }, [bmQ.data]);

  const defaultBlockSize = useMemo(() => {
    const s = bmSummary.block_size;
    if (Array.isArray(s) && s.length === 3) return s.map(Number);
    return [10, 10, 5];
  }, [bmSummary.block_size]);

  // Auto-pick a sensible vertical exaggeration when the block model is
  // very flat in Z (block Z ≪ block X/Y). Users can override manually.
  const autoVExagAppliedRef = useRef(false);
  useEffect(() => {
    if (autoVExagAppliedRef.current) return;
    if (vExagMode !== "preset") return;
    const [dx, dy, dz] = defaultBlockSize;
    if (!dx || !dz) return;
    const ratio = Math.min(dx, dy) / dz;
    if (ratio >= 8)      { setVExag(5); autoVExagAppliedRef.current = true; }
    else if (ratio >= 5) { setVExag(3); autoVExagAppliedRef.current = true; }
    else if (ratio >= 3) { setVExag(2); autoVExagAppliedRef.current = true; }
  }, [defaultBlockSize, vExagMode]);

  // ── LOD auto-switch by camera distance (Sprint P29 · Item 1) ──────
  // Users can force a level (0 = full, 1 = ½, 2 = ¼, …); "auto" toggles
  // stride based on camera-to-target distance vs bbox diagonal.
  const [lodMode,   setLodMode]   = useState("auto");   // "auto" | 0 | 1 | 2 | 3 | 4
  const [autoLevel, setAutoLevel] = useState(0);

  // ── LOD-strided block set (Sprint P29 · Item 1) ─────────────────────
  const lodStride = lodMode === "auto"
    ? Math.max(1, 2 ** autoLevel)
    : Math.max(1, 2 ** Number(lodMode));
  const blocksLOD = useMemo(() => {
    if (lodStride <= 1) return blocks;
    return blocks.filter((_, i) => i % lodStride === 0);
  }, [blocks, lodStride]);

  const holes = useMemo(() => holesQ.data?.holes || [], [holesQ.data]);

  // ── Stable scene origin (P0.4 · never recomputed on filter change) ──
  const originRef = useRef(null);
  const originKeyRef = useRef(null);
  const sceneOrigin = useMemo(() => {
    // Recompute origin only when the underlying (project, block-model-run)
    // changes — not when cut-off / filters / camera change.
    const runKey = `${pid}|${bmQ.data?.created_at || ""}|${bmQ.data?.summary?.method || ""}`;
    if (originRef.current && originKeyRef.current === runKey) {
      return originRef.current;
    }
    let originPts = null;
    if (blocks.length) {
      originPts = computeSceneOriginFromPoints(blocks);
    } else if (holes.length) {
      const collarPts = holes
        .filter((h) => h.x?.length && h.y?.length && h.z?.length)
        .map((h) => ({ x: h.x[0], y: h.y[0], z: h.z[0] }));
      originPts = computeSceneOriginFromPoints(collarPts);
    }
    if (!originPts) return null;
    originRef.current   = originPts;
    originKeyRef.current = runKey;
    return originPts;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: origin must NOT recompute on filter/cutoff changes; keyed by project+run identity via runKey
  }, [pid, bmQ.data?.created_at, bmQ.data?.summary?.method, blocks.length, holes.length]);

  const counts = {
    blocks: blocks.length,
    ore:    (bmQ.data?.ore_blocks || []).length,
    waste:  (bmQ.data?.waste_blocks || []).length,
    holes:  holes.length,
  };

  // ── World-space block bbox (used for section slicer + diagnostics) ──
  const worldBBox = useMemo(() => {
    if (!blocks.length) return null;
    let xMin = +Infinity, xMax = -Infinity;
    let yMin = +Infinity, yMax = -Infinity;
    let zMin = +Infinity, zMax = -Infinity;
    for (const b of blocks) {
      if (!isValidCoordinate(b.x, b.y, b.z)) continue;
      if (b.x < xMin) xMin = b.x; if (b.x > xMax) xMax = b.x;
      if (b.y < yMin) yMin = b.y; if (b.y > yMax) yMax = b.y;
      if (b.z < zMin) zMin = b.z; if (b.z > zMax) zMax = b.z;
    }
    if (xMin === +Infinity) return null;
    return { xMin, xMax, yMin, yMax, zMin, zMax };
  }, [blocks]);

  const slicerRange = useMemo(() => {
    if (!worldBBox) return [0, 100];
    if (slicer === "ns") return [worldBBox.xMin, worldBBox.xMax];
    if (slicer === "ew") return [worldBBox.yMin, worldBBox.yMax];
    return [0, 100];
  }, [worldBBox, slicer]);
  useEffect(() => {
    if (slicer === "off" || !slicerRange) return;
    if (slicerPos < slicerRange[0] || slicerPos > slicerRange[1]) {
      setSlicerPos((slicerRange[0] + slicerRange[1]) / 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- slicerPos intentionally omitted: including it would flip-flop the value against the range mid-drag; we only want to clamp when slicer mode or range boundaries change
  }, [slicer, slicerRange]);

  // ── Cutoff initialisation & bounds (P0.10 fix) ─────────────────────
  const cutoffMin = 0;
  const cutoffMax = useMemo(
    () => Math.max(1, ...blocks.map((b) => Number(b.grade ?? b.g ?? 0))),
    [blocks],
  );
  // Reporting cut-off = block-model summary (single source of truth for the
  // active estimation run) — falls back to project.cutoff_grade if summary
  // does not carry it. Never falls back to 0.
  const reportingCutoff = useMemo(() => {
    const s = bmSummary?.cutoff_grade;
    if (s != null && Number.isFinite(Number(s))) return Number(s);
    const p = projectQ.data?.cutoff_grade;
    if (p != null && Number.isFinite(Number(p))) return Number(p);
    // Commodity fallbacks (documented in Sprint P24 spec).
    const commodityFallback = {
      Au: 0.5, Ag: 30, Cu: 0.4, Zn: 3, Pb: 3, Ni: 0.3, Fe: 25, Li: 0.5,
    };
    return commodityFallback[projectQ.data?.commodity] ?? null;
  }, [bmSummary?.cutoff_grade, projectQ.data?.cutoff_grade, projectQ.data?.commodity]);

  useEffect(() => {
    // First-time cut-off init: only when neither URL nor user-manual has
    // set it, and reporting cutoff is available.
    if (cutoffInitRef.current) return;   // URL wins
    if (reportingCutoff == null) return;
    setCutoff(reportingCutoff);
    cutoffInitRef.current = true;
  }, [reportingCutoff]);

  // ── URL sync ──────────────────────────────────────────────────────
  useEffect(() => {
    const p = new URLSearchParams();
    p.set("palette", palette);
    if (Number.isFinite(cutoff)) p.set("cutoff", cutoff.toFixed(3));
    p.set("slicer", slicer);
    if (slicer !== "off") p.set("slicerPos", slicerPos.toFixed(0));
    const on = Object.entries(layers).filter(([, v]) => v).map(([k]) => k);
    p.set("layers", on.join(","));
    setSearchParams(p, { replace: true });
  }, [palette, cutoff, slicer, slicerPos, layers, setSearchParams]);

  // ── Local-space bounds per layer (populated by children callbacks) ─
  const [blockBounds, setBlockBounds] = useState(null);
  const [blockBoundsStats, setBlockBoundsStats] = useState({ rendered: 0, rejected: 0, invalid: 0 });
  const [holeBounds, setHoleBounds]   = useState(null);
  const [holeBoundsStats, setHoleBoundsStats] = useState({ count: 0 });

  // ── Outlier detection (holes whose collar is outside buffered bbox) ─
  const activeBBox = useMemo(() => {
    if (blockBounds && !blockBounds.isEmpty()) return bufferedBBox(blockBounds, 0.25);
    return null;
  }, [blockBounds]);

  const outlierHoles = useMemo(() => {
    if (!activeBBox || !sceneOrigin || !holes.length) return 0;
    let count = 0;
    for (const h of holes) {
      if (!h.x?.length) continue;
      if (!isValidCoordinate(h.x[0], h.y[0], h.z[0])) continue;
      const p = worldToScene(h.x[0], h.y[0], h.z[0], sceneOrigin, vExag);
      if (!pointInBox(p, activeBBox)) count++;
    }
    return count;
  }, [activeBBox, sceneOrigin, holes, vExag]);

  // ── Fit-mode → local bbox ─────────────────────────────────────────
  const computeFitBox = useCallback((mode) => {
    if (!sceneOrigin) return null;

    if (mode === "active") {
      // Fit tightly on the block model; include drillholes only if they
      // are within ~1.5× the block-model extent (so nearby holes show,
      // but far outliers do not zoom the view out to nothing).
      const box = new THREE.Box3();
      if (blockBounds && !blockBounds.isEmpty()) {
        box.union(blockBounds);
        if (holeBounds && !holeBounds.isEmpty()) {
          const size    = blockBounds.getSize(new THREE.Vector3());
          const centre  = blockBounds.getCenter(new THREE.Vector3());
          const bufferX = size.x * 0.75;
          const bufferY = size.y * 0.75;
          const bufferZ = size.z * 0.75;
          const buffered = new THREE.Box3(
            new THREE.Vector3(centre.x - size.x / 2 - bufferX,
                                centre.y - size.y / 2 - bufferY,
                                centre.z - size.z / 2 - bufferZ),
            new THREE.Vector3(centre.x + size.x / 2 + bufferX,
                                centre.y + size.y / 2 + bufferY,
                                centre.z + size.z / 2 + bufferZ),
          );
          const nearby = holeBounds.clone().intersect(buffered);
          if (!nearby.isEmpty()) box.union(nearby);
        }
      } else if (holeBounds && !holeBounds.isEmpty()) {
        // No blocks — fall back to hole bounds.
        box.union(holeBounds);
      }
      if (showMineDesignPit && pitDesignQ.data) {
        const pts = mineDesignPointStream(pitDesignQ.data);
        const { box: b } = computeLocalBoundingBox(pts, sceneOrigin, vExag);
        if (!b.isEmpty()) box.union(b);
      }
      // Iter98 · Stability Audit P2 — include VISIBLE geo meshes /
      // wireframes so the auto-fit never crops implicit shells, grade
      // shells or the topography surface out of view.
      for (const m of Object.values(geoLoadedMeshes)) {
        if (geoVisibility[m.id] !== true) continue;
        const pts = (m.vertices || []).map((v) => ({
          x: Number(v?.[0]), y: Number(v?.[1]), z: Number(v?.[2]),
        })).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z));
        if (!pts.length) continue;
        const { box: b } = computeLocalBoundingBox(pts, sceneOrigin, vExag);
        if (!b.isEmpty()) box.union(b);
      }
      return box.isEmpty() ? null : box;
    }

    if (mode === "all") {
      const box = new THREE.Box3();
      if (blockBounds && !blockBounds.isEmpty()) box.union(blockBounds);
      if (holeBounds && !holeBounds.isEmpty()) box.union(holeBounds);
      // Include mine-design + geo meshes if visible.
      if (showMineDesignPit && pitDesignQ.data) {
        const pts = mineDesignPointStream(pitDesignQ.data);
        const { box: b } = computeLocalBoundingBox(pts, sceneOrigin, vExag);
        if (!b.isEmpty()) box.union(b);
      }
      for (const m of Object.values(geoLoadedMeshes)) {
        if (geoVisibility[m.id] !== true) continue;
        const pts = (m.vertices || []).map((v) => ({
          x: Number(v?.[0]), y: Number(v?.[1]), z: Number(v?.[2]),
        })).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z));
        if (!pts.length) continue;
        const { box: b } = computeLocalBoundingBox(pts, sceneOrigin, vExag);
        if (!b.isEmpty()) box.union(b);
      }
      return box.isEmpty() ? null : box;
    }

    if (mode === "ore") {
      const orePts = (bmQ.data?.ore_blocks || []).map((b) => ({
        x: b.x, y: b.y, z: b.z,
        dx: b.dx || defaultBlockSize[0],
        dy: b.dy || defaultBlockSize[1],
        dz: b.dz || defaultBlockSize[2],
      }));
      const { box } = computeLocalBoundingBox(orePts, sceneOrigin, vExag);
      return box.isEmpty() ? null : box;
    }

    if (mode === "pit") {
      if (!pitDesignQ.data) return null;
      const pts = mineDesignPointStream(pitDesignQ.data);
      const { box } = computeLocalBoundingBox(pts, sceneOrigin, vExag);
      return box.isEmpty() ? null : box;
    }

    if (mode === "selected") {
      if (!selectedBlock) return null;
      const dx = Number(selectedBlock.dx || defaultBlockSize[0]);
      const dy = Number(selectedBlock.dy || defaultBlockSize[1]);
      const dz = Number(selectedBlock.dz || defaultBlockSize[2]);
      const { box } = computeLocalBoundingBox(
        [{ x: selectedBlock.x, y: selectedBlock.y, z: selectedBlock.z, dx, dy, dz }],
        sceneOrigin, vExag,
      );
      return box.isEmpty() ? null : box;
    }
    return null;
  }, [sceneOrigin, blockBounds, holeBounds,
       bmQ.data?.ore_blocks, defaultBlockSize, vExag,
       showMineDesignPit, pitDesignQ.data, selectedBlock,
       geoLoadedMeshes, geoVisibility]);

  // ── Fit triggers ──────────────────────────────────────────────────
  const requestFit = useCallback((mode) => {
    const box = computeFitBox(mode);
    if (!box) {
      setFitError(`No visible geometry for "${mode}"`);
      toast.error(`No visible geometry for "${mode}"`);
      return;
    }
    setFitError(null);
    setFitBusy(true);
    setFitMode(mode);
    setManualCamera(false);
    const direction = pendingViewDir || VIEW_DIRECTIONS.iso_ne;
    setFitRequest({ box, direction, ts: Date.now() });
  }, [computeFitBox, pendingViewDir]);

  const requestView = useCallback((viewKey) => {
    const dir = VIEW_DIRECTIONS[viewKey] || VIEW_DIRECTIONS.iso_ne;
    setPendingViewDir(dir);
    const box = computeFitBox(fitMode);
    if (box) {
      setFitBusy(true);
      setFitRequest({ box, direction: dir, ts: Date.now() });
    }
  }, [computeFitBox, fitMode]);

  const requestReset = useCallback(() => {
    setPendingViewDir(VIEW_DIRECTIONS.iso_ne);
    requestFit("active");
  }, [requestFit]);

  // Auto-fit whenever block bounds first appear (initial load). Also
  // re-fit once the drillhole bounds arrive so holes are always in view.
  const autoFitTriggeredRef = useRef(false);
  const holesFitTriggeredRef = useRef(false);
  useEffect(() => {
    if (!autoFitTriggeredRef.current) {
      if (blockBounds && !blockBounds.isEmpty()) {
        autoFitTriggeredRef.current = true;
        // Wait a tick for hole bounds to arrive too.
        setTimeout(() => requestFit("active"), 200);
      } else if (holeBounds && !holeBounds.isEmpty()) {
        // No blocks present yet — still fit on drillholes.
        autoFitTriggeredRef.current = true;
        setTimeout(() => requestFit("active"), 200);
        holesFitTriggeredRef.current = true;
      }
      return;
    }
    // Once the initial fit fired, re-fit on the first arrival of holes
    // (in case they loaded after the blocks) so they're always in view.
    if (!holesFitTriggeredRef.current
         && holeBounds && !holeBounds.isEmpty()) {
      holesFitTriggeredRef.current = true;
      setTimeout(() => requestFit("active"), 100);
    }
  }, [blockBounds, holeBounds, requestFit]);

  // Iter98 · Re-fit whenever the set of visible wireframes / geo meshes
  // grows so newly-toggled layers are always framed in view.
  const lastMeshCountRef = useRef(0);
  useEffect(() => {
    if (!autoFitTriggeredRef.current) return;
    const count = Object.values(geoLoadedMeshes)
      .filter((m) => geoVisibility[m.id] === true).length;
    if (count > lastMeshCountRef.current) {
      lastMeshCountRef.current = count;
      setTimeout(() => requestFit("active"), 150);
    } else {
      lastMeshCountRef.current = count;
    }
  }, [geoLoadedMeshes, geoVisibility, requestFit]);

  // Keyboard shortcut: F = Fit Active Model.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      // Fit shortcut
      if (e.key === "f" || e.key === "F") requestFit("active");
      // ESC → close currently open right-side dockable panel
      if (e.key === "Escape") {
        if (geoWorkspaceOpen) { setGeoWorkspaceOpen(false); e.preventDefault(); return; }
        if (showDiagnostics)  { setShowDiagnostics(false);  e.preventDefault(); return; }
      }
      // Ctrl+G → toggle Geo Model panel (single active right-side panel)
      if ((e.ctrlKey || e.metaKey) && (e.key === "g" || e.key === "G")) {
        e.preventDefault();
        setGeoWorkspaceOpen((o) => {
          const next = !o;
          if (next) setShowDiagnostics(false);   // enforce single-panel
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestFit, geoWorkspaceOpen, showDiagnostics]);

  // ── Persist right-panel state per user (P37) ─────────────────────
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem("sres.cockpit.geoPanelOpen");
      if (saved === "true") setGeoWorkspaceOpen(true);
      const pin = window.localStorage.getItem("sres.cockpit.geoPanelPinned");
      if (pin === "false") setGeoWorkspacePinned(false);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem("sres.cockpit.geoPanelOpen",
                                   geoWorkspaceOpen ? "true" : "false");
    } catch { /* ignore */ }
  }, [geoWorkspaceOpen]);
  useEffect(() => {
    try {
      window.localStorage.setItem("sres.cockpit.geoPanelPinned",
                                   geoWorkspacePinned ? "true" : "false");
    } catch { /* ignore */ }
  }, [geoWorkspacePinned]);

  // ── Click-outside-to-close when panel is in Auto-Hide (unpinned) ──
  useEffect(() => {
    if (!geoWorkspaceOpen || geoWorkspacePinned) return undefined;
    const onDocClick = (e) => {
      const panel  = document.querySelector('[data-testid="cockpit-geo-panel-container"]');
      const toggle = document.querySelector('[data-testid="cockpit-geo-toggle"]');
      if (!panel) return;
      if (panel.contains(e.target)) return;
      if (toggle && toggle.contains(e.target)) return;   // toggle handles itself
      setGeoWorkspaceOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [geoWorkspaceOpen, geoWorkspacePinned]);

  const onSnapshot = async () => {
    if (!takeSnapshotRef.current) return;
    // Ensure we are fitted before snapshotting.
    if (!fitRequest || fitError) {
      toast.warning("Fitting model before snapshot…");
      requestFit("active");
      await new Promise((r) => setTimeout(r, 500));
    }
    setSnapshotBusy(true);
    try {
      const blob = await takeSnapshotRef.current();
      if (!blob) throw new Error("Canvas capture returned empty");
      const fd = new FormData();
      fd.append("image", blob, "cockpit-snapshot.png");
      await api.post(`/projects/${pid}/cockpit-snapshot`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Snapshot uploaded — will appear on next CPR cover page");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Snapshot upload failed");
    } finally {
      setSnapshotBusy(false);
    }
  };

  // Iter98c · Capture the current 3D view into the CPR "Captured Views"
  // library (Appendix Y). Distinct from onSnapshot above (cover page):
  // this endpoint persists camera pose + colour mode metadata so the
  // CPR compiler can title and index each figure.
  const [captureViewBusy, setCaptureViewBusy] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const onCaptureView = async () => {
    if (!takeSnapshotRef.current) return;
    setCaptureViewBusy(true);
    try {
      const blob = await takeSnapshotRef.current();
      if (!blob) throw new Error("Canvas capture returned empty");
      const camState = cameraStateRef.current || {};
      const payload = {
        colour_mode:  colourBy || "grade",
        camera:       camState,
        vertical_exaggeration: vExag,
        caption:      `Cockpit capture · ${new Date().toISOString().slice(0, 19).replace("T", " ")} UTC`,
      };
      const fd = new FormData();
      fd.append("payload_json", JSON.stringify(payload));
      fd.append("file", blob, "captured-view.png");
      const r = await api.post(`/projects/${pid}/block-model/captured-view`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Captured view saved · ${r.data?.width}×${r.data?.height} px · will appear in CPR Appendix Y`,
      );
      // Iter98d · Refresh the gallery (if open) and auto-open it so the
      // user can immediately re-title / verify the newly captured view.
      qc.invalidateQueries({ queryKey: ["captured-views", pid] });
      setGalleryOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Capture view failed");
    } finally {
      setCaptureViewBusy(false);
    }
  };


  const onShare = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      setShareStatus("copied");
      toast.success("Deep-link copied to clipboard");
      setTimeout(() => setShareStatus("idle"), 2500);
    } catch {
      toast.error("Clipboard blocked — copy the URL manually");
    }
  };

  const isLoading = projectQ.isLoading || bmQ.isLoading || holesQ.isLoading;
  const unit = projectQ.data?.commodity === "Au" || projectQ.data?.commodity === "Ag" ? "g/t" : "%";

  // ── Saved-view state serialisation ─────────────────────────────────
  const currentViewState = () => {
    const cam = cameraCtlRef.current?.capture?.() || {};
    return {
      deep_link: window.location.search,
      layers, palette, cutoff, slicer, slicer_pos: slicerPos,
      camera: cam,
      v_exag: vExag,
      fit_mode: fitMode,
      project_version: projectQ.data?.version || null,
      model_version:   bmQ.data?.summary?.method || null,
    };
  };
  const applyView = (v) => {
    if (v.palette && COLORMAPS[v.palette])  setPalette(v.palette);
    if (typeof v.cutoff === "number") {
      setCutoff(v.cutoff);
      cutoffInitRef.current = true;
    }
    if (v.slicer)                           setSlicer(v.slicer);
    if (typeof v.slicer_pos === "number")   setSlicerPos(v.slicer_pos);
    if (v.layers && typeof v.layers === "object") setLayers(v.layers);
    if (typeof v.v_exag === "number") setVExag(v.v_exag);
    if (v.camera) {
      setManualCamera(true);
      cameraCtlRef.current?.apply?.(v.camera);
    }
    toast.success(`Opened "${v.name || "saved view"}"`);
  };

  // ── Diagnostics ──────────────────────────────────────────────────
  const [glError, setGlError] = useState(null);
  const rendererRef = useRef({ w: 0, h: 0 });
  const cameraStateRef = useRef({ pos: null, target: null, near: null, far: null });
  // LOD refs — updated in useMemo so callbacks see the latest.
  const diagonalRef  = useRef(0);
  const autoLevelRef = useRef(0);

  const diagnostics = useMemo(() => {
    const worldSize = worldBBox ? {
      x: worldBBox.xMax - worldBBox.xMin,
      y: worldBBox.yMax - worldBBox.yMin,
      z: worldBBox.zMax - worldBBox.zMin,
    } : null;
    const localSize = blockBounds && !blockBounds.isEmpty()
      ? blockBounds.getSize(new THREE.Vector3()) : null;
    const diagonal = localSize
      ? Math.hypot(localSize.x, localSize.y, localSize.z) : null;
    if (diagonal) diagonalRef.current = diagonal;
    const project = projectQ.data || {};
    const crsBits = [
      project.crs,
      project.epsg && `EPSG:${project.epsg}`,
      project.utm_zone && `UTM ${project.utm_zone}${project.hemisphere || ""}`,
    ].filter(Boolean);
    const geoMeshTotal = Object.keys(geoLoadedMeshes).length;
    const geoMeshVisible = Object.values(geoLoadedMeshes).filter(
      (m) => geoVisibility[m.id] === true).length;
    return {
      origin: sceneOrigin,
      worldBBox,
      worldSize, localSize, diagonal,
      crs:   crsBits.length ? crsBits.join(" · ") : null,
      datum: project.vertical_datum || project.datum || null,
      cameraPos:    cameraStateRef.current.pos,
      cameraTarget: cameraStateRef.current.target,
      near: cameraStateRef.current.near,
      far:  cameraStateRef.current.far,
      rendererSize: rendererRef.current,
      blocksRequested: blocks.length,
      blocksRendered:  blockBoundsStats.rendered,
      invalidBlocks:   blockBoundsStats.invalid,
      blocksFiltered:  blockBoundsStats.rejected,
      holesTotal: holes.length,
      holesActive: holeBoundsStats.count,
      outlierHoles,
      geoMeshCount:  geoMeshTotal,
      geoMeshVisible,
      mineDesignPresent: !!(pitDesignQ.data || mineSummaryQ.data?.engineering_pit),
      mineDesignVisible: showMineDesignPit && !!pitDesignQ.data,
      vExag,
      fitMode,
      glError,
      lodMode,
      lodLevel: lodMode === "auto" ? autoLevel : Number(lodMode),
      lodStride,
      blocksLODCount: blocksLOD.length,
    };
  }, [sceneOrigin, worldBBox, blockBounds, blocks.length, blockBoundsStats,
       holes.length, holeBoundsStats, outlierHoles, vExag, fitMode, glError,
       projectQ.data, geoLoadedMeshes, geoVisibility,
       pitDesignQ.data, mineSummaryQ.data, showMineDesignPit,
       lodMode, autoLevel, lodStride, blocksLOD.length]);

  const grid = useMemo(() => {
    if (!blockBounds || blockBounds.isEmpty()) return null;
    const size = blockBounds.getSize(new THREE.Vector3());
    const centre = blockBounds.getCenter(new THREE.Vector3());
    // Base grid on the horizontal span; place at model minimum elevation.
    const span = Math.max(size.x, size.z, 100);
    const cellSize = Math.max(10, Math.round(span / 60 / 10) * 10);
    const sectionSize = cellSize * 5;
    return {
      position: [centre.x, blockBounds.min.y - 1, centre.z],
      args: [span * 3, span * 3],
      cellSize,
      sectionSize,
    };
  }, [blockBounds]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-900" data-testid="cockpit-page">
      <ViewportToolbar project={projectQ.data}
              onExitCockpit={() => nav(`/app/projects/${pid}/wizard/block_model`)}
              onFitMode={requestFit}
              onViewChange={requestView}
              onManualCamera={() => { setManualCamera(true); toast.info("Manual camera — drag to fly"); }}
              onReset={requestReset}
              onSnapshot={onSnapshot}
              onCaptureView={onCaptureView}
              captureViewBusy={captureViewBusy}
              onOpenGallery={() => setGalleryOpen((v) => !v)}
              galleryOpen={galleryOpen}
              onShare={onShare}
              snapshotBusy={snapshotBusy}
              shareStatus={shareStatus}
              fitBusy={fitBusy}
              fitError={fitError} />

      <div className="flex-1 min-h-0 flex relative">
        <div className="w-56 shrink-0 flex flex-col bg-slate-950 border-r border-slate-800 text-slate-200">
          <ProjectExplorer counts={counts} layers={layers} setLayers={setLayers}
                            showMineDesignPit={showMineDesignPit}
                            setShowMineDesignPit={setShowMineDesignPit}
                            pitPresent={!!(pitDesignQ.data || mineSummaryQ.data?.engineering_pit)}
                            onFitOverlay={() => requestFit("pit")}
                            outlierHoles={outlierHoles}
                            includeOutliers={includeOutlierHoles}
                            setIncludeOutliers={setIncludeOutlierHoles} />
          <SavedViewsPanel pid={pid} currentState={currentViewState} onApply={applyView} />
          {/* Phase 2 · Feb 2026 · Quick jump to the Section Studio.
              Sits at the bottom of the left rail so it's always
              reachable while working with 3-D. */}
          <a
            href={`/app/projects/${pid}/sections`}
            className="mx-3 my-3 inline-flex items-center gap-1.5 justify-center rounded-none border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-[10.5px] font-semibold uppercase tracking-widest text-amber-300 hover:bg-amber-500/20 hover:border-amber-400 transition-colors"
            data-testid="cockpit-open-section-studio"
          >
            Open Section Studio
          </a>
        </div>

        <div className="flex-1 min-w-0 relative bg-[#0F1420]" data-testid="cockpit-canvas-wrapper">
          <CapturedViewsGallery pid={pid} open={galleryOpen}
                                    onClose={() => setGalleryOpen(false)} />
          {isLoading
            ? <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 text-sm gap-2 z-30">
                <div className="animate-pulse">Loading 3D scene…</div>
                <div className="text-[10px] font-mono text-slate-500">
                  {projectQ.isLoading && "· project · "}
                  {bmQ.isLoading && "· block model · "}
                  {holesQ.isLoading && "· drillholes ·"}
                </div>
              </div> : null}
          {!isLoading && blocks.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 text-sm gap-2 z-30">
              <Cube size={40} className="opacity-40" />
              No block model to display — build one in the Block Model step first.
            </div>
          )}
          {!isLoading && blocks.length > 0 && fitBusy && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 bg-slate-900/90 border border-brand-blue/40 rounded px-3 py-1 text-[11px] font-mono text-brand-blue">
              Fitting {fitMode === "active" ? "active model" : fitMode}…
            </div>
          )}
          {!isLoading && blocks.length > 0 && fitError && !fitBusy && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 bg-rose-950/90 border border-rose-500/60 rounded px-3 py-1 text-[11px] font-mono text-rose-200 flex items-center gap-2"
                 data-testid="cockpit-fit-error">
              <Warning size={12} weight="fill" />
              Unable to frame — {fitError}. Open Diagnostics for details.
            </div>
          )}

          <Canvas shadows
                  gl={{ preserveDrawingBuffer: true }}
                  camera={{ position: [500, 500, 500], fov: 45, near: 1, far: 1e7 }}
                  onCreated={({ gl }) => {
                    gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                    rendererRef.current = {
                      w: gl.domElement.clientWidth, h: gl.domElement.clientHeight,
                    };
                    // ResizeObserver keeps aspect + size in sync.
                    const ro = new ResizeObserver(() => {
                      const w = gl.domElement.clientWidth;
                      const h = gl.domElement.clientHeight;
                      if (w > 0 && h > 0) {
                        rendererRef.current = { w, h };
                      }
                    });
                    ro.observe(gl.domElement.parentElement);
                    gl.domElement.addEventListener("webglcontextlost", () =>
                      setGlError("context lost"));
                  }}
                  onError={(e) => setGlError(String(e))}
                  data-testid="cockpit-canvas">
            <color attach="background" args={["#0F1420"]} />
            {/* Iter98 · Studio-grade lighting rig — three-point + fill.
                Ambient stays modest so directional shadows read clearly. */}
            <ambientLight intensity={0.55} color="#E8ECF3" />
            <hemisphereLight args={["#8FA0BF", "#0B0F17", 0.55]} />
            <directionalLight position={[1200, 1800, 1000]} intensity={1.6}
                              color="#FFFFFF"
                              castShadow shadow-mapSize={[2048, 2048]} />
            {/* Fill light — cool cyan from the opposite side */}
            <directionalLight position={[-1000, 600, -800]} intensity={0.55}
                              color="#89B7FF" />
            {/* Rim / kicker — warm amber highlight from behind */}
            <directionalLight position={[0, -800, 1400]} intensity={0.35}
                              color="#FFC58B" />

            <SnapshotBridge onReady={(fn) => { takeSnapshotRef.current = fn; }} />
            <CameraBridge onReady={(ctl) => { cameraCtlRef.current = ctl; }} />
            <CameraTelemetry onUpdate={(state) => {
              cameraStateRef.current = state;
              // Auto LOD: pick a stride so that camera sees at most ~3000
              // instances at once.  Ratio = camera distance / bbox diagonal.
              if (lodMode !== "auto" || !state.pos || !state.target) return;
              const dx = state.pos.x - state.target.x;
              const dy = state.pos.y - state.target.y;
              const dz = state.pos.z - state.target.z;
              const dist = Math.hypot(dx, dy, dz);
              if (!diagonalRef.current || diagonalRef.current <= 0) return;
              const ratio = dist / diagonalRef.current;
              // 0 → close  (level 0 — full)
              // 1 → 1× diag (level 1 — ½)
              // 2 → 2× diag (level 2 — ¼)
              // 3 → 4× diag (level 3 — ⅛)
              // >5× diag    (level 4 — 1/16)
              const nextLevel =
                ratio < 0.75 ? 0 :
                ratio < 1.5  ? 1 :
                ratio < 3    ? 2 :
                ratio < 5    ? 3 : 4;
              // Debounce: only bump when the level actually changes.
              if (nextLevel !== autoLevelRef.current) {
                autoLevelRef.current = nextLevel;
                setAutoLevel(nextLevel);
              }
            }} />

            <FitCamera fitRequest={fitRequest}
                        onDone={(info) => {
                          setFitBusy(false); setFitError(null);
                          cameraStateRef.current.near = info.near;
                          cameraStateRef.current.far  = info.far;
                        }}
                        onFail={(reason) => {
                          setFitBusy(false); setFitError(reason);
                        }} />

            {/* Local-scene layers (all inside sceneOrigin frame) */}
            {sceneOrigin && (
              <group name="scene-local-frame"
                     userData={{ coordinateSpace: "local_scene" }}>
                {layers.blocks && (
                  <BlockMesh blocks={blocksLOD} palette={COLORMAPS[palette]}
                              cutoff={cutoff}
                              showOre={layers.ore !== false}
                              showWaste={layers.waste !== false}
                              slicer={slicer} slicerPos={slicerPos}
                              origin={sceneOrigin}
                              defaultBlockSize={defaultBlockSize}
                              verticalExaggeration={vExag}
                              zRange={zSlab} classSet={classFilter}
                              domainSet={domainFilter} minSamples={minSamples}
                              colourBy={colourBy}
                              onHoverBlock={(b, x, y) => {
                                setHoveredBlock(b);
                                if (b) setHoverPos({ x, y });
                              }}
                              onSelectBlock={(b) => {
                                setSelectedBlock(b);
                                toast.success(`Selected block · grade ${Number(b.grade).toFixed(3)} ${unit}`);
                              }}
                              onBounds={(box, stats) => {
                                setBlockBounds(box);
                                setBlockBoundsStats(stats);
                              }} />
                )}
                {/* Phase 1 · Feb 2026 · Interpolated grade-shell isosurface
                    (marching-cubes) — translucent gold shell that
                    follows the current cutoff slider. */}
                <GradeShellMesh pid={pid} cutoff={cutoff}
                                 origin={sceneOrigin}
                                 verticalExaggeration={vExag}
                                 visible={!!layers.shell} />
                {/* Phase 2 · Feb 2026 · Implicit wireframe from
                    drillhole intercepts (Leapfrog-style). */}
                <ImplicitWireframeMesh pid={pid} cutoff={cutoff}
                                        origin={sceneOrigin}
                                        verticalExaggeration={vExag}
                                        visible={!!layers.wireframe} />
                <DrillholeTraces holes={holes} showTraces={layers.traces}
                                  origin={sceneOrigin}
                                  verticalExaggeration={vExag}
                                  activeBBox={activeBBox}
                                  includeOutliers={includeOutlierHoles || fitMode === "all"}
                                  lineWidth={1.6}
                                  collarRadius={
                                    blockBounds && !blockBounds.isEmpty()
                                      ? Math.max(3, Math.min(20,
                                          Math.hypot(
                                            blockBounds.getSize(new THREE.Vector3()).x,
                                            blockBounds.getSize(new THREE.Vector3()).z,
                                          ) * 0.005))
                                      : 8
                                  }
                                  onBounds={(box, stats) => {
                                    setHoleBounds(box);
                                    setHoleBoundsStats(stats);
                                  }} />
                {/* Sprint P29 · Colour-coded 3D drillhole cylinders */}
                {layers.traces && holesQ.data?.samples?.length > 0 && (
                  <DrillholeMesh3D samples={holesQ.data.samples}
                                    holes={holes}
                                    showTraces
                                    origin={sceneOrigin}
                                    verticalExaggeration={vExag}
                                    activeBBox={activeBBox}
                                    includeOutliers={includeOutlierHoles || fitMode === "all"}
                                    colourMode={holeColourBy}
                                    palette={COLORMAPS[palette]}
                                    cutoff={cutoff}
                                    radius={
                                      blockBounds && !blockBounds.isEmpty()
                                        ? Math.max(2, Math.min(10,
                                            Math.hypot(
                                              blockBounds.getSize(new THREE.Vector3()).x,
                                              blockBounds.getSize(new THREE.Vector3()).z,
                                            ) * 0.0025))
                                        : 4
                                    }
                                    selectedHoleId={selectedHoleId}
                                    highlightedInterval={highlightedInterval}
                                    onSelectSample={(s) => {
                                      setSelectedHoleId(String(s.hole_id));
                                      toast.success(`Selected drillhole ${s.hole_id} · ${Number(s.grade).toFixed(3)} ${unit} @ ${s.from?.toFixed?.(1)}–${s.to?.toFixed?.(1)} m`);
                                    }} />
                )}
                <CollarLabels holes={holes} showLabels={layers.labels}
                              origin={sceneOrigin}
                              verticalExaggeration={vExag}
                              activeBBox={activeBBox}
                              includeOutliers={includeOutlierHoles || fitMode === "all"} />
                <SlicerPlane slicer={slicer} slicerPos={slicerPos}
                              worldBBox={worldBBox} origin={sceneOrigin}
                              verticalExaggeration={vExag} />

                {showMineDesignPit && pitDesignQ.data && (
                  <MineDesignLayer design={pitDesignQ.data} visibility
                                    origin={sceneOrigin}
                                    verticalExaggeration={vExag} />
                )}

                {Object.values(geoLoadedMeshes).map((m) => (
                  <GeoMeshLayer key={m.id} model={m}
                                visibility={geoVisibility[m.id] === true}
                                /* Presentation-grade rendering (Sprint P32.1):
                                   · topography stays as a shaded surface (0.5 opacity)
                                   · every other mesh (grade_shell / lithology_solid /
                                     implicit / explicit) renders as a THIN
                                     WIREFRAME at 15 % opacity so it never
                                     dominates the scene as a solid green blob.
                                   Users can still see the shape, but the block
                                   model + drillholes remain the visual focus. */
                                wireframe={m.kind !== "topography"}
                                opacity={m.kind === "topography" ? 0.5 : 0.15}
                                origin={sceneOrigin}
                                verticalExaggeration={vExag} />
                ))}
                {showAssayDiscs && (
                  <AssayDiscsLayer discs={assayDiscsQ.data?.discs} visibility
                                    origin={sceneOrigin}
                                    verticalExaggeration={vExag} />
                )}

                {/* Selected-block highlight outline */}
                {selectedBlock && sceneOrigin && (
                  <SelectedBlockOutline block={selectedBlock}
                                          origin={sceneOrigin}
                                          verticalExaggeration={vExag}
                                          defaultSize={defaultBlockSize} />
                )}

                {/* Grid inside local frame at model floor */}
                {layers.grid && grid && (
                  <Grid position={grid.position} args={grid.args}
                        cellSize={grid.cellSize} sectionSize={grid.sectionSize}
                        sectionColor="#3F5B8D" cellColor="#1E2C4A"
                        fadeDistance={Math.max(grid.args[0] * 0.6, 1000)}
                        fadeStrength={1.5}
                        infiniteGrid={false} />
                )}
              </group>
            )}

            <OrbitControls makeDefault enableDamping dampingFactor={0.08}
                            screenSpacePanning zoomToCursor
                            minPolarAngle={0} maxPolarAngle={Math.PI} />
            <GizmoHelper alignment="bottom-right" margin={[70, 70]}>
              <GizmoViewport axisColors={["#EF4444", "#22C55E", "#3B82F6"]}
                              labelColor="white" />
            </GizmoHelper>
          </Canvas>

          {/* Scale bar (screen overlay — outside translated frame) */}
          <div className="absolute bottom-3 left-3 bg-slate-900/80 border border-slate-700 rounded px-3 py-1.5 flex items-center gap-2 text-slate-300 text-[10px] font-mono"
                data-testid="cockpit-scalebar">
            <div className="w-10 h-1 bg-slate-300 rounded" />
            <div>{grid ? `${grid.cellSize} m/cell` : "100 m"}</div>
          </div>

          {/* Block Colour Legend (adapts to colour-by mode) */}
          <BlockLegend colourBy={colourBy} palette={COLORMAPS[palette] || COLORMAPS.viridis}
                        blocks={blocks} cutoff={cutoff} unit={unit} />

          {/* Drillhole Colour Legend (Sprint P29) */}
          {layers.traces && holesQ.data?.samples?.length > 0 && (
            <DrillholeLegend colourBy={holeColourBy} palette={COLORMAPS[palette]}
                              samples={holesQ.data.samples} cutoff={cutoff}
                              unit={unit} />
          )}

          {/* Info strip (dense HUD — CRS, counts, camera state) */}
          <div className="absolute top-3 left-3 bg-slate-950/85 border border-slate-700 rounded px-3 py-1.5 text-slate-300 text-[10px] font-mono flex items-center gap-4 backdrop-blur"
               data-testid="cockpit-status">
            <span>CRS · <b className="text-sky-300">{diagnostics.crs || "local"}</b></span>
            <span>Blocks · <b className="text-emerald-400">{blockBoundsStats.rendered || counts.blocks}</b>/<b>{counts.blocks}</b></span>
            <span>Ore · <b className="text-amber-400">{counts.ore}</b></span>
            <span>Holes · <b className="text-sky-400">{holeBoundsStats.count || counts.holes}</b>/<b>{counts.holes}</b></span>
            {outlierHoles > 0 && (
              <span className="text-amber-300">Outliers · <b>{outlierHoles}</b></span>
            )}
            {slicer !== "off" && (
              <span>Slicer · <b className="text-sky-400">{slicer.toUpperCase()} @ {slicerPos.toFixed(0)} m</b></span>
            )}
            <span className="text-slate-400">Fit · {fitMode}</span>
            <span className="text-slate-400">V×{vExag}</span>
            <span className="text-slate-400"
                   data-testid="cockpit-lod-label">
              LOD ·{" "}
              <select value={lodMode}
                       onChange={(e) => setLodMode(e.target.value)}
                       className="bg-slate-900 border border-slate-700 rounded px-1 text-slate-100 font-mono text-[10px]"
                       data-testid="cockpit-lod-select">
                <option value="auto">auto (L{autoLevel} · s{lodStride})</option>
                <option value="0">L0 · full</option>
                <option value="1">L1 · ½</option>
                <option value="2">L2 · ¼</option>
                <option value="3">L3 · ⅛</option>
                <option value="4">L4 · 1⁄16</option>
              </select>
            </span>
            <button onClick={() => setShowDiagnostics((v) => {
                      const next = !v;
                      if (next) setGeoWorkspaceOpen(false);
                      return next;
                    })}
                    className="ml-1 text-sky-400 hover:text-white flex items-center gap-1"
                    data-testid="cockpit-diag-toggle">
              <Info size={11} weight="duotone" />
              <span className="uppercase tracking-widest">{showDiagnostics ? "Hide diag" : "Diagnostics"}</span>
            </button>
          </div>

          {/* Rich block-hover tooltip */}
          {hoveredBlock && (
            <div className="pointer-events-none absolute z-40 bg-slate-900/95 border border-brand-blue/60 rounded shadow-xl px-3 py-2 text-[11px] font-mono text-slate-100"
                  style={{ left: hoverPos.x + 12, top: hoverPos.y + 12, minWidth: 220 }}
                  data-testid="cockpit-block-tooltip">
              <div className="text-brand-blue font-bold uppercase tracking-widest text-[9px] mb-1">
                Block
              </div>
              <BlockTooltipRows b={hoveredBlock} cutoff={cutoff} unit={unit} />
            </div>
          )}

          {/* Diagnostics panel */}
          {showDiagnostics && (
            <DiagnosticsPanel diag={diagnostics}
                              onClose={() => setShowDiagnostics(false)} />
          )}
        </div>

        <InspectorPanel palette={palette} setPalette={setPalette}
                        cutoff={cutoff} setCutoff={setCutoff}
                        cutoffMin={cutoffMin} cutoffMax={cutoffMax}
                        reportingCutoff={reportingCutoff}
                        cutoffMode={cutoffMode} setCutoffMode={setCutoffMode}
                        unit={unit}
                        slicer={slicer} setSlicer={setSlicer}
                        slicerPos={slicerPos} setSlicerPos={setSlicerPos}
                        slicerRange={slicerRange}
                        colourBy={colourBy} setColourBy={setColourBy}
                        holeColourBy={holeColourBy} setHoleColourBy={setHoleColourBy}
                        minSamples={minSamples} setMinSamples={setMinSamples}
                        classFilter={classFilter} setClassFilter={setClassFilter}
                        zSlab={zSlab} setZSlab={setZSlab}
                        zRange={worldBBox ? [worldBBox.zMin, worldBBox.zMax] : null}
                        vExag={vExag} setVExag={setVExag}
                        vExagMode={vExagMode} setVExagMode={setVExagMode} />

        {/* Geological Modelling right pane — dockable panel (P37) */}
        <div className={`shrink-0 overflow-hidden transition-all duration-200 ease-in-out ${
              geoWorkspaceOpen ? "w-72" : "w-0"
            }`}
             data-testid="cockpit-geo-panel-container"
             aria-hidden={!geoWorkspaceOpen}>
          {geoWorkspaceOpen && (
            <CockpitGeoWorkspace projectId={pid}
                                   visibility={geoVisibility}
                                   onModelVisibilityToggle={toggleGeoVisibility}
                                   onModelSelect={(id) => setGeoVisibility((prev) => ({ ...prev, [id]: true }))}
                                   showAssayDiscs={showAssayDiscs}
                                   onAssayDiscsToggle={setShowAssayDiscs}
                                   pinned={geoWorkspacePinned}
                                   onPinToggle={() => setGeoWorkspacePinned((p) => !p)}
                                   onClose={() => setGeoWorkspaceOpen(false)} />
          )}
        </div>
        <button type="button"
                onClick={() => setGeoWorkspaceOpen((o) => {
                  const next = !o;
                  if (next) setShowDiagnostics(false);
                  return next;
                })}
                data-testid="cockpit-geo-toggle"
                title="Geo Model panel (Ctrl+G · Esc to close)"
                aria-pressed={geoWorkspaceOpen}
                aria-expanded={geoWorkspaceOpen}
                className="absolute top-16 right-0 z-30 bg-brand-blue text-white text-[9px] uppercase tracking-widest font-mono px-2 py-3 rounded-l shadow-lg hover:bg-brand-blue/90 transition-colors"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          {geoWorkspaceOpen ? "Close" : "Geo Model"}
        </button>
      </div>
    </div>
  );
}


